import React, { useMemo } from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc } from "@editor/utils/trpc";

import { Background, ReactFlow } from "@xyflow/react";

import "@xyflow/react/dist/style.css";

import EditorSandpackProvider from "@/features/react-editor/components/EditorSandpackProvider";
import { useIframeMessaging } from "@/features/react-editor/useIframeMessaging";
import { SandpackPreview } from "@codesandbox/sandpack-react";
import IconButton from "@replo/design-system/components/button/IconButton";
import { skipToken } from "@tanstack/react-query";
import { Pencil } from "lucide-react";

import { generateNodesCanvas } from "../utils/react-flow-helpers";
import FunnelPagesMenu from "./FunnelPagesMenu";

function FunnelEditor({ funnelId }: { funnelId: string }) {
  const projectId = useCurrentProjectId();
  useIframeMessaging();
  const { data: funnel, refetch: refetchFunnel } = trpc.funnel.find.useQuery(
    {
      projectId: projectId ?? "",
      funnelId,
    },
    {
      enabled: Boolean(projectId && funnelId),
    },
  );

  const nodes = generateNodesCanvas(funnel?.funnelSteps ?? [], projectId ?? "");

  const { data: allFiles = [], isLoading: isLoadingFiles } =
    trpc.file.list.useQuery(projectId ? { projectId } : skipToken);

  // ToDo: this should only get the product ids that are being used in the pages
  const { data: products } = trpc.product.list.useQuery(
    projectId ? { projectId, pageSize: 100 } : skipToken,
  );
  const productIds = products?.products.map((product) => product.id);

  const openPages =
    funnel?.funnelSteps.map((step) => step.file?.path ?? "") ?? [];

  const nodeTypes = useMemo(() => ({ "page-canvas": PageCanvasNode }), []);
  const { mutate: updateFunnelStepOrder } =
    trpc.funnel.updateFunnelStepOrder.useMutation({
      onSuccess: () => {
        void refetchFunnel();
      },
    });

  if (isLoadingFiles) {
    return <div>Loading files...</div>;
  }

  return (
    <div className="flex h-full w-full items-center justify-center rounded-lg bg-white">
      <EditorSandpackProvider
        files={allFiles}
        openPages={openPages}
        productIds={productIds ?? []}
        displayMode="preview"
        sandpackLayoutClassName="flex h-full w-full flex-wrap gap-3 relative"
      >
        <ReactFlow
          nodes={nodes}
          nodeTypes={nodeTypes}
          fitView
          style={{
            backgroundColor: "#fff",
          }}
        >
          <Background />
        </ReactFlow>

        <FunnelPagesMenu
          funnelId={funnelId}
          updateFunnelStepOrder={updateFunnelStepOrder}
        />
      </EditorSandpackProvider>
    </div>
  );
}

const PageCanvasNode = ({
  data,
}: {
  data: { path: string; name: string; projectId: string; pageId: string };
}) => {
  const { path, name, projectId, pageId } = data;

  return (
    <div className="border border-border flex flex-wrap rounded-lg flex-col h-fit p-3 bg-white">
      <div className="flex flex-row w-full justify-between">
        <div className="flex flex-col gap-1">
          <div className="text-default text-base font-semibold">{name}</div>
          <div className="text-sm text-muted mb-3">{path}</div>
        </div>
        <div>
          <IconButton
            icon={<Pencil size={12} />}
            size="sm"
            variant="secondary"
            onClick={() => {
              window.open(`/shops/${projectId}/pages/${pageId}`, "_blank");
            }}
          />
        </div>
      </div>
      <div className="h-[400px] w-[300px] border border-secondary rounded-lg overflow-hidden">
        <div className="origin-top-left scale-[0.3] w-[1000px] h-[1500px] pointer-events-none">
          <SandpackPreview
            showOpenInCodeSandbox={false}
            showRefreshButton={false}
            startRoute={path}
            className="w-full h-full"
          />
        </div>
      </div>
    </div>
  );
};

export default FunnelEditor;
